<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import BasicIconButton from '~/components/form-elements/BasicIconButton.vue'
import BasicToCartButton from '~/components/form-elements/BasicToCartButton.vue'
import DolyameButton from '~/components/form-elements/DolyameButton.vue'
import QuantityCounter from '~/components/form-elements/QuantityCounter.vue'
import Multiplicity from '~/components/products/Multiplicity.vue'
import ProductAsideRowLink from '~/components/products/ProductAsideRowLink.vue'
import ProductText from '~/components/products/ProductText.vue'
import { useProduct } from '~/composables/product'
import constants from '~/constants'
import formatter from '~/helpers/formatter'
import { useAuthStore } from '~/store/auth'
import { useCartStore } from '~/store/cart'
import { useProductsStore } from '~/store/products'
import type { CategoryData } from '~/types/categories'
import type { ProductData } from '~/types/products'

const props = defineProps<{
  productData: ProductData
}>()

const authStore = useAuthStore()
const cartStore = useCartStore()
const productsStore = useProductsStore()

const { addProductToCart, toggleIsInWishList, setCartProductQuantity } = useProduct()

const canBeOrdered = computed(() => !isOutOfStock.value || isOnPreOrder.value)
const cashbackInPoints = computed(() => props.productData.meta.cashbackInPoints)
const isCertificate = computed(
  () => props.productData.relationships.categories.data.some(
    (x: CategoryData): boolean => x.id === constants.categories.certificates.id
  )
)
const isInWishList = computed(() => props.productData.meta.isInWishList)
const isOnPreOrder = computed(() => props.productData.meta.isOnPreOrder)
const isOutOfStock = computed(() => props.productData.meta.isOutOfStock)
const multiplicity = computed(() => props.productData.attributes.multiplicity ?? 1)
const price = computed(() => props.productData.attributes.price)
const quantity = computed({
  get (): number {
    return productsStore.getCartProductQuantity(props.productData.id) ?? 0
  },
  set (quantity: number): void {
    setCartProductQuantity(props.productData.id, quantity)
  }
})
</script>

<template>
  <DefaultBox class="w-full lg:w-[385px] xl:w-[443px]">
    <PriceWithPromoCode mode="product-page" :product-data="productData" />

    <Cashback
      v-if="typeof cashbackInPoints === 'number' && cashbackInPoints !== 0"
      :cashback-in-points="cashbackInPoints"
    />

    <Multiplicity class="mt-2" :multiplicity="multiplicity" />

    <div id="product-next-button" class="mt-4 flex items-center md:mt-6">
      <div v-if="quantity" class="flex items-center">
        <BasicToCartButton class="mr-2" />

        <QuantityCounter
          v-model="quantity"
          class="!h-[44px] !w-[106px] justify-center space-x-4 md:!h-[56px] md:!w-[126px] md:space-x-6"
          is-wide-on-mobile
          :min="0"
          :multiplicity="multiplicity"
        />
      </div>
      <BasicButton
        v-else
        class="!h-[44px] w-full md:!h-[56px]"
        color="green"
        :is-disabled="!canBeOrdered"
        :title="'В\xA0корзину'"
        @click="addProductToCart(productData.id, multiplicity)"
      />

      <BasicIconButton
        class="ml-2"
        :icon="isInWishList ? 'outlined/favorite' : 'outlined/favorite-border'"
        @click.prevent="toggleIsInWishList(productData)"
      />
    </div>

    <DolyameButton
      v-if="constants.isDolyameEnabled && !isCertificate"
      class="mb-6 md:mt-4"
      is-wide-on-mobile
      :price="price"
    />

    <div id="aside-body" />

    <ProductAsideRowLink
      class="mb-3 mt-6 border-b border-[#E2E2E2] pb-3 md:mt-4"
      label="Пункты выдачи"
      :text="'От\xA03\xA0дней'"
    >
      <SvgIcon class="-mr-4" name="shipping-types/yandex-delivery-rounded" />
      <SvgIcon class="-mr-4" name="shipping-types/cdek-rounded" />
      <SvgIcon class="-mr-4" name="shipping-types/boxberry-rounded" />

      <b
        class="
          flex items-center justify-center rounded-full border border-[#F7F7F7] bg-white font-normal
          text-main-dark text-opacity-50
        "
      >
        +8
      </b>
    </ProductAsideRowLink>

    <ProductAsideRowLink class="mb-7" label="Самовывоз" text="Завтра">
      <SvgIcon class="mr-2 !size-[45px]" name="brands/itab-rounded" />
    </ProductAsideRowLink>

    <template
      v-if="
        authStore.isUser
          && (
            (cartStore.cashbackInPoints !== null && cartStore.cashbackInPoints !== 0)
            || (cartStore.cashbackInSuperminsPoints !== null && cartStore.cashbackInSuperminsPoints !== 0)
          )
      "
    >
      <div class="mb-2 flex items-center">
        <ProductText class="!mr-1 block" :text="'Начислим за\xA0заказ'" />

        <!-- TODO: ... -->
        <!--        <a-->
        <!--          class="!no-underline"-->
        <!--          href="#"-->
        <!--        >-->
        <!--          <SvgIcon class="!size-4" name="outlined/help-strong" />-->
        <!--        </a>-->
      </div>

      <table
        class="text-sm font-medium leading-[1.43] tracking-[-0.01em] text-main-dark md:text-base md:leading-normal"
      >
        <tbody>
          <tr v-if="cartStore.cashbackInPoints !== null && cartStore.cashbackInPoints !== 0">
            <th>
              Баллы iTAB Плюс
            </th>
            <td>
              <div class="flex items-center justify-end">
                <SvgIcon
                  class="!text-base !text-main-green md:!text-xl"
                  name="outlined/itab-reverse"
                />

                <span
                  class="ml-1 inline-block"
                >
                  {{ formatter.formatPrice(cartStore.cashbackInPoints) }}
                </span>
              </div>
            </td>
          </tr>

          <tr
            v-if="cartStore.cashbackInSuperminsPoints !== null && cartStore.cashbackInSuperminsPoints !== 0"
            class="mt-1"
          >
            <th>
              Баллы Supermins
            </th>
            <td>
              <div class="flex items-center justify-end">
                <SvgIcon
                  class="!text-base !text-[#FFB524] md:!text-xl"
                  name="outlined/supermins"
                />

                <span
                  class="ml-1 inline-block"
                >
                  {{ formatter.formatPrice(cartStore.cashbackInSuperminsPoints) }}
                </span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <div id="aside-footer" />
  </DefaultBox>
</template>
